import axios from 'axios';
import config from './../config';
import ApiErrorHandler from '../api/ApiErrorHandler';
import AuthService from '../Auth/AuthService';

const CSRF_METHODS = ['post', 'put', 'patch', 'delete'];
const isCsrfProtected = requestConfig => CSRF_METHODS.includes(requestConfig?.method?.toLowerCase());

const singleton = Symbol();
const singletonEnforcer = Symbol();
const axiosWithGlobalHandler = Symbol();
const axiosWithoutGlobalHandler = Symbol();

export default class $http {
  constructor(enforcer) {
    if (enforcer !== singletonEnforcer) {
      throw new Error('Cannot construct singleton');
    }

    // The session is the JSESSIONID cookie the backend sets after Sangoma SSO login, so credentials must be sent
    this[axiosWithGlobalHandler] = axios.create({ withCredentials: true });
    this[axiosWithoutGlobalHandler] = axios.create({ withCredentials: true });

    [this[axiosWithGlobalHandler], this[axiosWithoutGlobalHandler]].forEach(instance =>
      instance.interceptors.request.use(
        async config => {
          Object.assign(config, this.header);

          if (isCsrfProtected(config)) {
            // Waits for the token request if it is still running, or makes it
            const token = await AuthService.loadCsrfToken();

            config.headers[AuthService.getCsrfHeaderName()] = token;
          }

          return config;
        },
        error => Promise.reject(error)
      )
    );

    this[axiosWithGlobalHandler].interceptors.response.use(
      response => Promise.resolve(response),
      error => {
        // skip handling of canceled requests
        if (error.message === 'canceled') {
          return Promise.reject(error);
        }

        return ApiErrorHandler(error);
      }
    );

    // Requests that skip the global error handler still have to open the login modal on "unauthorized"
    this[axiosWithoutGlobalHandler].interceptors.response.use(
      response => response,
      error => {
        if (AuthService.isUnauthorizedError(error)) {
          AuthService.showLogin();
        }

        return Promise.reject(error);
      }
    );
  }

  get apiDomainURI() {
    return config.api.url;
  }

  get api() {
    return this[axiosWithGlobalHandler];
  }

  get apiWithoutGlobalHandler() {
    return this[axiosWithoutGlobalHandler];
  }

  static get instance() {
    if (!this[singleton]) {
      this[singleton] = new $http(singletonEnforcer);
    }

    return this[singleton];
  }

  get header() {
    return {
      headers: {
        'Content-Type': 'application/json',
        UI_Version: config.version,
      },
    };
  }
}
